<script>
import { defineAsyncComponent } from 'vue';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import MessageApi from 'dashboard/api/inbox/message';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ReportCaptainMessageDialog from './ReportCaptainMessageDialog.vue';

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
    Spinner,
    EmojiIconPicker,
    ReportCaptainMessageDialog,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      showMediaModal: false,
      showReactionPicker: false,
      isSendingReaction: false,
      reactionEmojis: ['👍', '❤️', '😂', '🫠', '🥳', '🥹', '👏', '🥰'],
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    businessReaction() {
      return (
        this.contentAttributes.reactions?.find(
          reaction => reaction.actor_type === 'business'
        )?.emoji || ''
      );
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.showReactionPicker = false;
      this.$emit('close', e);
    },
    async handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      try {
        await this.$store.dispatch('translateMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          targetLanguage,
        });
        useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      } catch (error) {
        useAlert(parseAPIErrorResponse(error));
      }
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    async handleReaction(emoji) {
      if (this.isSendingReaction) return;
      this.isSendingReaction = true;
      this.showReactionPicker = false;
      const nextEmoji = this.businessReaction === emoji ? '' : emoji;

      try {
        await MessageApi.react(this.conversationId, this.messageId, nextEmoji);
        const notification = nextEmoji
          ? this.$t('CONVERSATION.CONTEXT_MENU.REACTION.SUCCESS')
          : this.$t('CONVERSATION.CONTEXT_MENU.REACTION.REMOVED');
        useAlert(notification);
      } catch (error) {
        useAlert(parseAPIErrorResponse(error));
      } finally {
        this.isSendingReaction = false;
        this.handleClose();
      }
    },
    selectReaction(selection) {
      this.showReactionPicker = false;
      this.handleReaction(selection.value);
    },
    async setMediaVisibility(hidden) {
      try {
        const { data } = await MessageApi.setMediaVisibility(
          this.conversationId,
          this.messageId,
          hidden
        );
        this.$store.dispatch('updateMessage', data);
        useAlert(
          this.$t(
            hidden
              ? 'CONVERSATION.CONTEXT_MENU.HIDE_MEDIA_SUCCESS'
              : 'CONVERSATION.CONTEXT_MENU.SHOW_MEDIA_SUCCESS'
          )
        );
      } catch (error) {
        useAlert(parseAPIErrorResponse(error));
      }
    },
    async handleHideMedia() {
      this.handleClose();
      await this.setMediaVisibility(true);
    },
    openShowMediaModal() {
      this.handleClose();
      this.showMediaModal = true;
    },
    async confirmShowMedia() {
      this.showMediaModal = false;
      await this.setMediaVisibility(false);
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    openReportDialog() {
      this.handleClose();
      this.$refs.reportDialog?.open();
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <!-- Confirm showing media marked as inappropriate -->
    <woot-delete-modal
      v-if="showMediaModal && enabledOptions['showMedia']"
      v-model:show="showMediaModal"
      class="context-menu--delete-modal"
      :on-close="() => (showMediaModal = false)"
      :on-confirm="confirmShowMedia"
      :title="$t('CONVERSATION.CONTEXT_MENU.SHOW_MEDIA_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.SHOW_MEDIA_CONFIRMATION.MESSAGE')"
      :confirm-text="
        $t('CONVERSATION.CONTEXT_MENU.SHOW_MEDIA_CONFIRMATION.CONFIRM')
      "
      :reject-text="
        $t('CONVERSATION.CONTEXT_MENU.SHOW_MEDIA_CONFIRMATION.CANCEL')
      "
    />
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <div v-if="enabledOptions['reaction']" class="reaction-picker-wrap">
          <div
            class="reaction-picker"
            :aria-busy="isSendingReaction"
            :aria-label="$t('CONVERSATION.CONTEXT_MENU.REACTION.LABEL')"
          >
            <button
              v-for="emoji in reactionEmojis"
              :key="emoji"
              type="button"
              class="reaction-picker__button"
              :class="{
                'reaction-picker__button--active': businessReaction === emoji,
              }"
              :title="$t('CONVERSATION.CONTEXT_MENU.REACTION.LABEL')"
              :disabled="isSendingReaction"
              @click.stop="handleReaction(emoji)"
            >
              {{ emoji }}
            </button>
            <button
              type="button"
              class="reaction-picker__button reaction-picker__more"
              :title="$t('CONVERSATION.CONTEXT_MENU.REACTION.MORE')"
              :aria-label="$t('CONVERSATION.CONTEXT_MENU.REACTION.MORE')"
              :aria-expanded="showReactionPicker"
              :disabled="isSendingReaction"
              @click.stop="showReactionPicker = !showReactionPicker"
            >
              <span class="i-lucide-plus size-4" />
            </button>
          </div>
          <div
            v-if="isSendingReaction"
            class="flex items-center gap-2 px-3 py-2 text-xs text-n-slate-11"
            role="status"
          >
            <Spinner :show-message="false" :size="16" />
            {{ $t('GENERAL.LOADING') }}
          </div>
          <EmojiIconPicker
            v-if="showReactionPicker"
            mode="emoji"
            class="!top-11 ltr:!right-0 rtl:!left-0 z-[100]"
            @select="selectReaction"
          />
        </div>
        <hr v-if="enabledOptions['reaction']" />
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <hr />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="enabledOptions['hideMedia'] || enabledOptions['showMedia']" />
        <MenuItem
          v-if="enabledOptions['hideMedia']"
          :option="{
            icon: 'i-hugeicons-view-off-slash',
            label: $t('CONVERSATION.CONTEXT_MENU.HIDE_MEDIA'),
          }"
          variant="icon"
          @click.stop="handleHideMedia"
        />
        <MenuItem
          v-if="enabledOptions['showMedia']"
          :option="{
            icon: 'i-hugeicons-view',
            label: $t('CONVERSATION.CONTEXT_MENU.SHOW_MEDIA'),
          }"
          variant="icon"
          @click.stop="openShowMediaModal"
        />
        <hr v-if="enabledOptions['report']" />
        <MenuItem
          v-if="enabledOptions['report']"
          :option="{
            icon: 'warning',
            label: $t('CONVERSATION.CONTEXT_MENU.REPORT_MESSAGE.LABEL'),
          }"
          variant="icon"
          @click.stop="openReportDialog"
        />
        <hr v-if="enabledOptions['delete']" />
        <MenuItem
          v-if="enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
      </div>
    </ContextMenu>
    <ReportCaptainMessageDialog
      v-if="enabledOptions['report']"
      ref="reportDialog"
      :message-id="messageId"
    />
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply mx-2 my-2 h-0 border-0 border-t border-solid border-n-weak;
  }
}

.reaction-picker {
  @apply flex items-center gap-0.5 px-1.5 py-1;
}

.reaction-picker-wrap {
  @apply relative;
}

.reaction-picker__button {
  @apply disabled:cursor-wait disabled:opacity-50 grid size-8 shrink-0 cursor-pointer place-items-center rounded-lg text-base transition hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand;
}

.reaction-picker__button--active {
  @apply bg-n-brand/10 ring-1 ring-n-brand/35;
}

.reaction-picker__more {
  @apply ms-1 rounded-full border border-solid border-n-weak bg-n-alpha-1 text-n-slate-11 hover:border-n-brand/40 hover:bg-n-brand/10 hover:text-n-brand;

  &[aria-expanded='true'] {
    @apply border-n-brand/40 bg-n-brand/10 text-n-brand;
  }
}

.context-menu--delete-modal {
  :deep(.modal-container) {
    @apply max-w-[30rem];

    h2 {
      @apply font-medium text-base;
    }
  }
}
</style>
